import React, { useEffect, useState } from 'react';
import { apiClient } from '../api/client';
import { TransactionWithAsset } from '../types';
import { formatINR } from '../utils/currency';

const TransactionHistory: React.FC = () => {
  const [transactions, setTransactions] = useState<TransactionWithAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getTransactions();
      setTransactions(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load transactions');
      console.error('Error fetching transactions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (transactionId: number) => {
    setDeleteId(transactionId);
    setShowConfirm(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;

    try {
      await apiClient.deleteTransaction(deleteId);
      fetchTransactions();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete transaction');
    } finally {
      setShowConfirm(false);
      setDeleteId(null);
    }
  };

  const cancelDelete = () => {
    setShowConfirm(false);
    setDeleteId(null);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="rounded-xl border border-white/10 bg-white/5 p-4 animate-pulse">
            <div className="h-4 bg-white/10 rounded w-3/4 mb-2"></div>
            <div className="h-3 bg-white/10 rounded w-1/2"></div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-6">
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className="rounded-xl border border-white/10 bg-white/5 p-12 text-center">
        <p className="text-slate-400 text-lg mb-2">No transactions yet</p>
        <p className="text-slate-500 text-sm">Record your first BUY or SELL to see transaction history</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">Transaction History</h2>
        <span className="text-sm text-slate-400">{transactions.length} transaction{transactions.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="space-y-2">
        {transactions.map((transaction) => {
          const quantity = parseFloat(transaction.quantity);
          const pricePerUnit = parseFloat(transaction.price_per_unit);
          const fees = parseFloat(transaction.fees);
          const totalAmount = quantity * pricePerUnit + fees;
          const isBuy = transaction.transaction_type === 'BUY';

          return (
            <div
              key={transaction.id}
              className="rounded-lg border border-white/10 bg-white/5 p-4 hover:bg-white/10 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`px-3 py-1 text-sm font-bold rounded ${
                      isBuy ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {transaction.transaction_type}
                    </span>
                    <div>
                      <p className="font-semibold">{transaction.asset.symbol}</p>
                      <p className="text-xs text-slate-400">{transaction.asset.name}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <p className="text-slate-500 text-xs">Quantity</p>
                      <p className="font-medium">{quantity.toFixed(4)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 text-xs">Price/Unit</p>
                      <p className="font-medium">{formatINR(pricePerUnit)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 text-xs">Total Amount</p>
                      <p className="font-medium">{formatINR(totalAmount)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 text-xs">Date</p>
                      <p className="font-medium">{new Date(transaction.transaction_date).toLocaleDateString()}</p>
                    </div>
                  </div>

                  {fees > 0 && (
                    <p className="text-xs text-slate-500 mt-2">Fees: {formatINR(fees)}</p>
                  )}

                  {transaction.notes && (
                    <p className="text-xs text-slate-400 mt-2 italic">{transaction.notes}</p>
                  )}
                </div>

                <button
                  onClick={() => handleDelete(transaction.id)}
                  className="ml-4 px-3 py-1 text-sm bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded transition-colors"
                  title="Delete transaction"
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg border border-white/10 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirm Delete</h3>
            <p className="text-slate-300 mb-6">
              Are you sure you want to delete this transaction? This may affect your holdings calculations.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionHistory;